import { datadogRum, RumEvent } from '@datadog/browser-rum';
import type { RuntimeConfig } from './runtimeConfig';

let initialized = false;

// MSAL redirects carry auth codes/state in the hash and query; never send them to Datadog.
export function stripUrl(url: string): string {
  return url.split(/[?#]/)[0];
}

export function scrubEvent(event: RumEvent): boolean {
  if (event.view) {
    event.view.url = stripUrl(event.view.url);
    if (event.view.referrer) event.view.referrer = stripUrl(event.view.referrer);
  }
  if (event.type === 'resource') event.resource.url = stripUrl(event.resource.url);
  if (event.type === 'error' && event.error.resource) {
    event.error.resource.url = stripUrl(event.error.resource.url);
  }
  return true;
}

/**
 * Starts Datadog RUM only when the runtime config supplies an application ID and
 * client token (dev/ppr). Otherwise it does nothing, so production is unaffected.
 */
export function initDatadogRum(config?: Partial<RuntimeConfig>): boolean {
  const applicationId = config?.datadogRumApplicationId;
  const clientToken = config?.datadogRumClientToken;
  if (initialized || !applicationId || !clientToken) return false;

  try {
    const apiUrl = config?.apiUrl;
    datadogRum.init({
      applicationId,
      clientToken,
      site: config?.datadogSite || 'datadoghq.eu',
      service: 'adm-fe',
      env: config?.datadogEnv || config?.environment,
      version: config?.appVersion,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: apiUrl
        ? [{ match: (url: string) => url.startsWith(apiUrl), propagatorTypes: ['tracecontext'] }]
        : [],
      beforeSend: scrubEvent,
    });
    initialized = true;
  } catch (error) {
    console.warn('Datadog RUM initialisation failed', error);
  }
  return initialized;
}
